// Combined Analytics Service
// Serviço que combina Google Analytics e Umami Analytics

import analyticsService from './analytics';
import umamiAnalyticsService from './umamiAnalytics';

class CombinedAnalyticsService {
  constructor() {
    this.googleAnalytics = analyticsService;
    this.umamiAnalytics = umamiAnalyticsService;
  }

  // Verificar quais serviços estão disponíveis
  getAvailableServices() {
    return {
      googleAnalytics: this.googleAnalytics.isAvailable(),
      umamiAnalytics: this.umamiAnalytics.isAvailable()
    };
  }

  // Rastrear evento em ambos os serviços
  trackEvent(eventName, parameters = {}) {
    // Google Analytics
    if (this.googleAnalytics.isAvailable()) {
      this.googleAnalytics.trackEvent(eventName, parameters);
    }

    // Umami Analytics
    if (this.umamiAnalytics.isAvailable()) {
      this.umamiAnalytics.trackEvent(eventName, parameters);
    }
  }

  // Rastrear visualização de página
  trackPageView(pagePath, pageTitle) {
    // Google Analytics
    if (this.googleAnalytics.isAvailable()) {
      this.googleAnalytics.trackPageView(pagePath, pageTitle);
    }

    // Umami Analytics (automático, mas pode ser chamado manualmente)
    if (this.umamiAnalytics.isAvailable()) {
      this.umamiAnalytics.trackPageView(pagePath, pageTitle);
    }
  }

  // Rastrear login
  trackLogin(method = 'email') {
    this.googleAnalytics.trackLogin(method);
    this.umamiAnalytics.trackLogin(method);
  }

  // Rastrear logout
  trackLogout() {
    this.googleAnalytics.trackLogout();
    this.umamiAnalytics.trackLogout();
  }

  // Rastrear clique em botão
  trackButtonClick(buttonName, section = '') {
    this.googleAnalytics.trackButtonClick(buttonName, section);
    this.umamiAnalytics.trackButtonClick(buttonName, section);
  }

  // Rastrear navegação no menu
  trackNavigation(destination) {
    this.googleAnalytics.trackNavigation(destination);
    this.umamiAnalytics.trackNavigation(destination);
  }

  // Rastrear interação com WhatsApp
  trackWhatsAppInteraction(action, instanceName = '') {
    this.googleAnalytics.trackWhatsAppInteraction(action, instanceName);
    this.umamiAnalytics.trackWhatsAppInteraction(action, instanceName);
  }

  // Rastrear uso do CRM
  trackCRMAction(action, contactId = '') {
    this.googleAnalytics.trackCRMAction(action, contactId);
    this.umamiAnalytics.trackCRMAction(action, contactId);
  }

  // Rastrear visualização de dashboard
  trackDashboardView(dashboardType) {
    this.googleAnalytics.trackDashboardView(dashboardType);
    this.umamiAnalytics.trackDashboardView(dashboardType);
  }

  // Rastrear erro
  trackError(errorType, errorMessage = '') {
    this.googleAnalytics.trackError(errorType, errorMessage);
    this.umamiAnalytics.trackError(errorType, errorMessage);
  }

  // Rastrear tempo de sessão
  trackSessionDuration(duration) {
    this.googleAnalytics.trackSessionDuration(duration);
    this.umamiAnalytics.trackSessionDuration(duration);
  }

  // Rastrear conversão/meta
  trackConversion(conversionName, value = 0) {
    this.googleAnalytics.trackConversion(conversionName, value);
    this.umamiAnalytics.trackConversion(conversionName, value);
  }

  // Rastrear download de arquivo
  trackDownload(fileName, fileType = '') {
    this.googleAnalytics.trackDownload(fileName, fileType);
    this.umamiAnalytics.trackDownload(fileName, fileType);
  }

  // Rastrear busca
  trackSearch(searchTerm, resultsCount = 0) {
    this.googleAnalytics.trackSearch(searchTerm, resultsCount);
    this.umamiAnalytics.trackSearch(searchTerm, resultsCount);
  }

  // Definir propriedades do usuário
  setUserProperties(properties) {
    this.googleAnalytics.setUserProperties(properties);
    this.umamiAnalytics.setUserProperties(properties);
  }

  // Definir ID do usuário
  setUserId(userId) {
    this.googleAnalytics.setUserId(userId);
    this.umamiAnalytics.setUserId(userId);
  }

  // Rastrear timing personalizado
  trackTiming(category, variable, value, label = '') {
    this.googleAnalytics.trackTiming(category, variable, value, label);
    this.umamiAnalytics.trackTiming(category, variable, value, label);
  }

  // Obter status de ambos os serviços
  getStatus() {
    return {
      googleAnalytics: {
        available: this.googleAnalytics.isAvailable(),
        measurementId: this.googleAnalytics.GA_MEASUREMENT_ID
      },
      umamiAnalytics: {
        ...this.umamiAnalytics.getStatus()
      }
    };
  }

  // Método para debug - mostrar status no console
  debugStatus() {
    const status = this.getStatus();
    console.log('📊 Status dos Analytics:', status);
    return status;
  }
}

// Criar instância única do serviço combinado
const combinedAnalyticsService = new CombinedAnalyticsService();

export default combinedAnalyticsService;

// Exportar também métodos individuais para facilitar o uso
export const {
  trackEvent,
  trackPageView,
  trackLogin,
  trackLogout,
  trackButtonClick,
  trackNavigation,
  trackWhatsAppInteraction,
  trackCRMAction,
  trackDashboardView,
  trackError,
  trackSessionDuration,
  trackConversion,
  trackDownload,
  trackSearch,
  setUserProperties,
  setUserId,
  trackTiming,
  getStatus,
  debugStatus
} = combinedAnalyticsService;